import { useState, useEffect, useRef } from 'react'
import {
  Box,
  VStack,
  HStack,
  Text,
  Button,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  IconButton,
  useDisclosure,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  Input,
  FormControl,
  FormLabel,
  useToast,
  Badge,
  Spinner,
  AlertDialog,
  AlertDialogOverlay,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogBody,
  AlertDialogFooter,
  Checkbox,
  CheckboxGroup,
  Avatar,
  AvatarGroup,
  Wrap,
  WrapItem,
  useColorModeValue,
  useBreakpointValue,
  Drawer,
  DrawerOverlay,
  DrawerContent,
  DrawerCloseButton,
  DrawerBody,
} from '@chakra-ui/react'
import { Plus, PencilSimple, Trash, Users, Folders, List } from '@phosphor-icons/react'
import api from '../services/api'
import { Sidebar } from '../layouts/DefaultLayout'
import socketService, { SOCKET_EVENTS } from '../services/socket'

interface Agent {
  id: number
  name: string
  email: string
  profilePic: string | null
}

interface Category {
  id: number
  name: string
}

interface Group {
  id: number
  name: string
  members: Agent[]
  categories: Category[]
  memberCount: number
  categoryCount: number
}

export default function Groups() {
  const [groups, setGroups] = useState<Group[]>([])
  const [agents, setAgents] = useState<Agent[]>([])
  const [categories, setCategories] = useState<Category[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [selectedGroup, setSelectedGroup] = useState<Group | null>(null)
  const [formData, setFormData] = useState({
    name: '',
    memberIds: [] as number[],
    categoryIds: [] as number[],
  })
  const [internalTicketsBadge, setInternalTicketsBadge] = useState(0)
  const toast = useToast()
  const isMobile = useBreakpointValue({ base: true, lg: false })
  const surfaceBg = useColorModeValue('white', 'gray.800')
  const subtleBg = useColorModeValue('gray.100', 'gray.700')
  const borderColor = useColorModeValue('gray.200', 'gray.700')
  const textPrimary = useColorModeValue('gray.800', 'white')
  const textMuted = useColorModeValue('gray.500', 'gray.400')
  const menuIconColor = useColorModeValue('gray.700', 'white')

  const { isOpen: isFormOpen, onOpen: onFormOpen, onClose: onFormClose } = useDisclosure()
  const { isOpen: isDeleteOpen, onOpen: onDeleteOpen, onClose: onDeleteClose } = useDisclosure()
  const { isOpen: isMenuOpen, onOpen: onMenuOpen, onClose: onMenuClose } = useDisclosure()
  const cancelRef = useRef<HTMLButtonElement>(null)

  const fetchInternalTicketsCount = async () => {
    try {
      const res = await api.get('/internal-tickets/my-pending-count')
      setInternalTicketsBadge(res.data.count || 0)
    } catch {
      // ignore
    }
  }

  useEffect(() => {
    fetchInternalTicketsCount()
    const interval = setInterval(fetchInternalTicketsCount, 120000)

    const handleNew = () => fetchInternalTicketsCount()
    const handleReply = () => fetchInternalTicketsCount()
    const handleResolved = () => fetchInternalTicketsCount()

    socketService.on(SOCKET_EVENTS.INTERNAL_TICKET.NEW, handleNew)
    socketService.on(SOCKET_EVENTS.INTERNAL_TICKET.REPLY, handleReply)
    socketService.on(SOCKET_EVENTS.INTERNAL_TICKET.RESOLVED, handleResolved)

    return () => {
      clearInterval(interval)
      socketService.off(SOCKET_EVENTS.INTERNAL_TICKET.NEW, handleNew)
      socketService.off(SOCKET_EVENTS.INTERNAL_TICKET.REPLY, handleReply)
      socketService.off(SOCKET_EVENTS.INTERNAL_TICKET.RESOLVED, handleResolved)
    }
  }, [])

  const fetchData = async () => {
    setIsLoading(true)
    try {
      const [groupsRes, agentsRes, categoriesRes] = await Promise.all([
        api.get('/admin/groups'),
        api.get('/admin/groups/agents/available'),
        api.get('/admin/categories'),
      ])
      setGroups(groupsRes.data)
      setAgents(agentsRes.data)
      setCategories(categoriesRes.data)
    } catch (error) {
      console.error('Error fetching data:', error)
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    fetchData()
  }, [])

  const handleCreate = () => {
    setSelectedGroup(null)
    setFormData({
      name: '',
      memberIds: [],
      categoryIds: [],
    })
    onFormOpen()
  }

  const handleEdit = (group: Group) => {
    setSelectedGroup(group)
    const availableAgentIds = new Set(agents.map((a) => a.id))
    const availableCategoryIds = new Set(categories.map((c) => c.id))
    setFormData({
      name: group.name,
      memberIds: group.members.map((m) => m.id).filter((id) => availableAgentIds.has(id)),
      categoryIds: group.categories.map((c) => c.id).filter((id) => availableCategoryIds.has(id)),
    })
    onFormOpen()
  }

  const handleDelete = (group: Group) => {
    setSelectedGroup(group)
    onDeleteOpen()
  }

  const handleSubmit = async () => {
    if (!formData.name.trim()) {
      toast({ title: 'Nome é obrigatório', status: 'warning', duration: 3000 })
      return
    }
    const availableAgentIds = new Set(agents.map((a) => a.id))
    const availableCategoryIds = new Set(categories.map((c) => c.id))
    const memberIds = formData.memberIds.filter((id) => availableAgentIds.has(id))
    const categoryIds = formData.categoryIds.filter((id) => availableCategoryIds.has(id))
    if (memberIds.length === 0) {
      toast({ title: 'Selecione pelo menos um atendente da lista disponível', status: 'warning', duration: 3000 })
      return
    }
    if (categoryIds.length === 0) {
      toast({ title: 'Selecione pelo menos uma categoria da lista disponível', status: 'warning', duration: 3000 })
      return
    }

    const payload = { name: formData.name.trim(), memberIds, categoryIds }

    try {
      if (selectedGroup) {
        await api.put(`/admin/groups/${selectedGroup.id}`, payload)
        toast({ title: 'Grupo atualizado', status: 'success', duration: 3000 })
      } else {
        await api.post('/admin/groups', payload)
        toast({ title: 'Grupo criado', status: 'success', duration: 3000 })
      }
      onFormClose()
      fetchData()
    } catch (error: any) {
      const data = error.response?.data
      const baseMessage = data?.error || 'Tente novamente'
      const parts: string[] = [baseMessage]
      if (data?.invalidMemberIds?.length) {
        const names =
          agents.length > 0
            ? data.invalidMemberIds
                .map((id: number) => agents.find((a) => a.id === id)?.name || `ID ${id}`)
                .join(', ')
            : data.invalidMemberIds.join(', ')
        parts.push(`Sem permissão de suporte: ${names}.`)
      }
      if (data?.invalidCategoryIds?.length) {
        const names =
          categories.length > 0
            ? data.invalidCategoryIds
                .map((id: number) => categories.find((c) => c.id === id)?.name || `ID ${id}`)
                .join(', ')
            : data.invalidCategoryIds.join(', ')
        parts.push(`Categorias inexistentes: ${names}.`)
      }
      toast({
        title: 'Erro',
        description: parts.join(' '),
        status: 'error',
        duration: 5000,
      })
    }
  }

  const handleConfirmDelete = async () => {
    if (!selectedGroup) return

    try {
      await api.delete(`/admin/groups/${selectedGroup.id}`)
      toast({ title: 'Grupo excluído', status: 'success', duration: 3000 })
      onDeleteClose()
      fetchData()
    } catch (error: any) {
      toast({
        title: 'Erro ao excluir',
        description: error.response?.data?.error || 'Tente novamente',
        status: 'error',
        duration: 5000,
      })
    }
  }

  return (
    <>
      {/* Mobile menu drawer */}
      {isMobile && (
        <Drawer isOpen={isMenuOpen} placement="left" onClose={onMenuClose}>
          <DrawerOverlay />
          <DrawerContent bg={surfaceBg} maxW="280px">
            <DrawerCloseButton />
            <DrawerBody p={0}>
              <Sidebar onClose={onMenuClose} internalTicketsBadge={internalTicketsBadge} />
            </DrawerBody>
          </DrawerContent>
        </Drawer>
      )}

      <Box p={{ base: 3, md: 6 }} h="100%" overflowY="auto">
        <VStack align="stretch" spacing={{ base: 4, md: 6 }}>
          <HStack justify="space-between" spacing={3} flexWrap={{ base: 'wrap', md: 'nowrap' }}>
            <HStack spacing={3} align="center" flex={1} minW={0}>
              {isMobile && (
                <IconButton
                  aria-label="Menu"
                  icon={<List size={20} />}
                  variant="ghost"
                  onClick={onMenuOpen}
                  size="sm"
                  color={menuIconColor}
                  _hover={{ bg: borderColor }}
                  flexShrink={0}
                />
              )}
              <VStack align="start" spacing={0} flex={1} minW={0}>
                <Text fontSize={{ base: 'lg', md: '2xl' }} fontWeight="bold" color={textPrimary}>
                  Grupos de Atendentes
                </Text>
                <Text fontSize={{ base: 'xs', md: 'sm' }} color={textMuted} display={{ base: 'none', md: 'block' }}>
                  Configure quais atendentes atendem quais categorias
                </Text>
              </VStack>
            </HStack>
            <Button
              leftIcon={<Plus size={18} />}
              colorScheme="yellow"
              onClick={handleCreate}
              size={{ base: 'sm', md: 'md' }}
              flexShrink={0}
            >
              <Text display={{ base: 'none', sm: 'block' }}>Novo Grupo</Text>
              <Text display={{ base: 'block', sm: 'none' }}>Novo</Text>
            </Button>
          </HStack>

        {isLoading ? (
          <Spinner size="xl" color="yellow.500" alignSelf="center" />
        ) : isMobile ? (
          <VStack spacing={3} align="stretch">
            {groups.map((group) => (
              <Box
                key={group.id}
                bg={surfaceBg}
                p={4}
                borderRadius="lg"
                border="1px"
                borderColor={borderColor}
              >
                <VStack align="stretch" spacing={3}>
                  <HStack justify="space-between" align="start">
                    <Text color={textPrimary} fontWeight="semibold" fontSize="sm" flex={1} minW={0}>
                      {group.name}
                    </Text>
                    <HStack spacing={1} flexShrink={0}>
                      <IconButton
                        aria-label="Editar"
                        icon={<PencilSimple size={16} />}
                        size="xs"
                        variant="ghost"
                        onClick={() => handleEdit(group)}
                      />
                      <IconButton
                        aria-label="Excluir"
                        icon={<Trash size={16} />}
                        size="xs"
                        variant="ghost"
                        colorScheme="red"
                        onClick={() => handleDelete(group)}
                      />
                    </HStack>
                  </HStack>
                  <HStack justify="space-between" align="center">
                    <HStack spacing={2}>
                      <Users size={14} color={textMuted} />
                      <Text fontSize="xs" color={textMuted}>
                        Atendentes:
                      </Text>
                    </HStack>
                    <HStack spacing={2}>
                      <AvatarGroup size="xs" max={3}>
                        {group.members.map((member) => (
                          <Avatar
                            key={member.id}
                            name={member.name}
                            src={member.profilePic || undefined}
                            bg="gray.600"
                          />
                        ))}
                      </AvatarGroup>
                      <Badge colorScheme="blue" fontSize="xs">{group.memberCount}</Badge>
                    </HStack>
                  </HStack>
                  <HStack justify="space-between" align="start">
                    <HStack spacing={2}>
                      <Folders size={14} color={textMuted} />
                      <Text fontSize="xs" color={textMuted}>
                        Categorias:
                      </Text>
                    </HStack>
                    <Wrap spacing={1} flex={1} justify="flex-end">
                      {group.categories.slice(0, 2).map((cat) => (
                        <WrapItem key={cat.id}>
                          <Badge colorScheme="purple" fontSize="xs">
                            {cat.name}
                          </Badge>
                        </WrapItem>
                      ))}
                      {group.categoryCount > 2 && (
                        <WrapItem>
                          <Badge colorScheme="gray" fontSize="xs">
                            +{group.categoryCount - 2}
                          </Badge>
                        </WrapItem>
                      )}
                    </Wrap>
                  </HStack>
                </VStack>
              </Box>
            ))}
          </VStack>
        ) : (
          <Box bg={surfaceBg} borderRadius="xl" overflow="hidden" overflowX="auto">
            <Table>
              <Thead>
                <Tr>
                  <Th color={textMuted}>Nome</Th>
                  <Th color={textMuted}>Atendentes</Th>
                  <Th color={textMuted}>Categorias</Th>
                  <Th color={textMuted} w="100px">Ações</Th>
                </Tr>
              </Thead>
              <Tbody>
                {groups.map((group) => (
                  <Tr key={group.id}>
                    <Td>
                      <Text color={textPrimary} fontWeight="medium">
                        {group.name}
                      </Text>
                    </Td>
                    <Td>
                      <HStack spacing={2}>
                        <AvatarGroup size="sm" max={3}>
                          {group.members.map((member) => (
                            <Avatar
                              key={member.id}
                              name={member.name}
                              src={member.profilePic || undefined}
                              bg="gray.600"
                            />
                          ))}
                        </AvatarGroup>
                        <Badge colorScheme="blue">{group.memberCount}</Badge>
                      </HStack>
                    </Td>
                    <Td>
                      <Wrap spacing={1}>
                        {group.categories.slice(0, 3).map((cat) => (
                          <WrapItem key={cat.id}>
                            <Badge colorScheme="purple" fontSize="xs">
                              {cat.name}
                            </Badge>
                          </WrapItem>
                        ))}
                        {group.categoryCount > 3 && (
                          <WrapItem>
                            <Badge colorScheme="gray" fontSize="xs">
                              +{group.categoryCount - 3}
                            </Badge>
                          </WrapItem>
                        )}
                      </Wrap>
                    </Td>
                    <Td>
                      <HStack spacing={2}>
                        <IconButton
                          aria-label="Editar"
                          icon={<PencilSimple size={18} />}
                          size="sm"
                          variant="ghost"
                          onClick={() => handleEdit(group)}
                        />
                        <IconButton
                          aria-label="Excluir"
                          icon={<Trash size={18} />}
                          size="sm"
                          variant="ghost"
                          colorScheme="red"
                          onClick={() => handleDelete(group)}
                        />
                      </HStack>
                    </Td>
                  </Tr>
                ))}
              </Tbody>
            </Table>
          </Box>
        )}
      </VStack>

      {/* Form Modal */}
      <Modal isOpen={isFormOpen} onClose={onFormClose} size={{ base: 'full', sm: 'xl' }}>
        <ModalOverlay />
        <ModalContent bg={surfaceBg}>
          <ModalHeader>{selectedGroup ? 'Editar' : 'Novo'} Grupo</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack spacing={6}>
              <FormControl>
                <FormLabel>Nome do Grupo</FormLabel>
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="Ex: Suporte Técnico, Financeiro..."
                />
              </FormControl>

              <FormControl>
                <FormLabel>
                  <HStack>
                    <Users size={18} />
                    <Text>Atendentes</Text>
                  </HStack>
                </FormLabel>
                <Box
                  maxH="200px"
                  overflowY="auto"
                  p={3}
                  bg={subtleBg}
                  borderRadius="md"
                >
                  <CheckboxGroup
                    value={formData.memberIds.map(String)}
                    onChange={(values) =>
                      setFormData({
                        ...formData,
                        memberIds: values.map((v) => Number(v)),
                      })
                    }
                  >
                    <VStack align="start" spacing={2}>
                      {agents.map((agent) => (
                        <Checkbox key={agent.id} value={String(agent.id)}>
                          <HStack>
                            <Avatar
                              size="xs"
                              name={agent.name}
                              src={agent.profilePic || undefined}
                            />
                            <Text fontSize="sm">{agent.name}</Text>
                            <Text fontSize="xs" color={textMuted}>
                              ({agent.email})
                            </Text>
                          </HStack>
                        </Checkbox>
                      ))}
                    </VStack>
                  </CheckboxGroup>
                </Box>
                <Text fontSize="xs" color={textMuted} mt={1}>
                  Selecionados: {formData.memberIds.length}
                </Text>
              </FormControl>

              <FormControl>
                <FormLabel>
                  <HStack>
                    <Folders size={18} />
                    <Text>Categorias Atendidas</Text>
                  </HStack>
                </FormLabel>
                <Box
                  maxH="200px"
                  overflowY="auto"
                  p={3}
                  bg={subtleBg}
                  borderRadius="md"
                >
                  <CheckboxGroup
                    value={formData.categoryIds.map(String)}
                    onChange={(values) =>
                      setFormData({
                        ...formData,
                        categoryIds: values.map((v) => Number(v)),
                      })
                    }
                  >
                    <VStack align="start" spacing={2}>
                      {categories.map((cat) => (
                        <Checkbox key={cat.id} value={String(cat.id)}>
                          <Text fontSize="sm">{cat.name}</Text>
                        </Checkbox>
                      ))}
                    </VStack>
                  </CheckboxGroup>
                </Box>
                <Text fontSize="xs" color={textMuted} mt={1}>
                  Selecionadas: {formData.categoryIds.length}
                </Text>
              </FormControl>
            </VStack>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={onFormClose}>
              Cancelar
            </Button>
            <Button colorScheme="yellow" onClick={handleSubmit}>
              {selectedGroup ? 'Salvar' : 'Criar'}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Delete Confirmation */}
      <AlertDialog
        isOpen={isDeleteOpen}
        leastDestructiveRef={cancelRef}
        onClose={onDeleteClose}
      >
        <AlertDialogOverlay>
          <AlertDialogContent bg={surfaceBg}>
            <AlertDialogHeader>Excluir Grupo</AlertDialogHeader>
            <AlertDialogBody>
              Tem certeza que deseja excluir o grupo "{selectedGroup?.name}"?
              <br />
              <Text color="yellow.400" fontSize="sm" mt={2}>
                Os atendentes não serão excluídos, apenas removidos do grupo.
              </Text>
            </AlertDialogBody>
            <AlertDialogFooter>
              <Button ref={cancelRef} onClick={onDeleteClose}>
                Cancelar
              </Button>
              <Button colorScheme="red" onClick={handleConfirmDelete} ml={3}>
                Excluir
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
      </Box>
    </>
  )
}
